import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ButtonGroup } from '@frame/ui-components/button-group'
import { Footer } from '@frame/ui-components/footer'
import { P } from '@frame/ui-primitives/p'

import { INCOMPLETE_TASK_HELPER_TEXT } from '@frame/components/screens/tasks/constants'
import { VerificationType } from '@frame/lib/api/client-session/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { buildEmployerHistoryViewProps } from '@frame/lib/api/events/utils'
import { DirectTask } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useEmitComplete } from '@frame/lib/hooks/useEmitComplete'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useStore } from '@frame/lib/stores/useStore'
import { isMarkedComplete, isSkippedTask, isVerifiedViaInstant } from '@frame/lib/utils/tasks'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

type Props = {
  directTasks: DirectTask[]
  setShowIncompletePrompt: (value: boolean) => void
}

export function TaskFooter({ setShowIncompletePrompt, directTasks }: Props) {
  const navigate = useNavigate()
  const { api } = useRootContext()
  const { handleEvent } = useEventHandler()
  const emitComplete = useEmitComplete()
  const { bilEnabledStrategies } = useStrategies()
  const { maxEmployments, autoComplete, cobrandingLogoUrl, reportType } = useStore(({ session, config }) => ({
    maxEmployments: session?.max_employments ?? 1,
    autoComplete: config.autoComplete,
    cobrandingLogoUrl: session?.configuration.cobranding.logo_url,
    reportType: session?.verification_type,
  }))
  const [isCompletingSession, setIsCompletingSession] = useState(false)

  const canAddEmployer = directTasks.length < maxEmployments
  const hasSkippedEmployments = directTasks.some((task) => isSkippedTask(task))

  async function completeSession() {
    if (hasSkippedEmployments) {
      setShowIncompletePrompt(true)
    } else if (autoComplete) {
      setIsCompletingSession(true)
      await api.clientSession.completeSession()
      handleEvent(
        InternalEventType.SHARED_EMPLOYER_DATA,
        buildEmployerHistoryViewProps(canAddEmployer, bilEnabledStrategies, directTasks)
      )
      emitComplete()
      navigate(routes.complete, { replace: true })
    }
  }

  const hasOnlyInstantEmployments = directTasks.every(isVerifiedViaInstant)
  const allEmploymentsCompleted = directTasks.length > 0 && directTasks.every(isMarkedComplete)
  const hasIncompleteTask = directTasks.some((task) => !isMarkedComplete(task))
  const hasMoreThanOneIncompleteTask = directTasks.filter((task) => !isMarkedComplete(task)).length > 1
  const showSubmitHelperText =
    (hasIncompleteTask && hasSkippedEmployments) ||
    (hasMoreThanOneIncompleteTask && reportType !== VerificationType.TENANT_SCREENING)

  if (showSubmitHelperText) {
    return (
      <footer data-cy={DataAttribute.FOOTER} className="p-5 text-center text-sm font-normal text-moss-500">
        <P>{INCOMPLETE_TASK_HELPER_TEXT}</P>
      </footer>
    )
  }

  if (!allEmploymentsCompleted) {
    return null
  }

  return (
    <Footer
      showPoweredByTruework={Boolean(cobrandingLogoUrl)}
      buttons={ButtonGroup({ completeSession, isCompletingSession, hasOnlyInstantEmployments })}
    />
  )
}
